/**
 * Display helpers for QMS records.
 * Prefer backend `recordCode` when present; otherwise derive a short display code from the UUID.
 */

import { format } from "date-fns";
import type { CAPARecord } from "@/types/qms.type";

/** Backend CAPA text fields require MinLength(10). */
export const QMS_CAPA_TEXT_MIN_LENGTH = 10;

export const formatQmsRecordCode = (
  id: string,
  recordCode?: string | null,
): string => {
  if (recordCode?.trim()) {
    return recordCode.trim();
  }
  const short = id.replace(/-/g, "").slice(0, 8).toUpperCase();
  return `QMS-${short}`;
};

/** Format API/ISO dates for UI (e.g. 17-Aug-2026). Returns "—" when empty/invalid. */
export const formatQmsDisplayDate = (
  value?: string | null,
  emptyLabel = "—",
): string => {
  if (!value?.trim()) {
    return emptyLabel;
  }
  const trimmed = value.trim();
  // Prefer calendar YYYY-MM-DD without UTC shift for date-only values.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    const local = new Date(year, month, day);
    if (Number.isNaN(local.getTime())) {
      return emptyLabel;
    }
    return format(local, "dd-MMM-yyyy");
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return emptyLabel;
  }
  return format(parsed, "dd-MMM-yyyy");
};

export const isQmsRecordOverdue = (
  dueDate: string,
  status: string,
): boolean => {
  if (status === "Closed") {
    return false;
  }
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

/** Derive CAPA owner initials for API preview (max 10 chars). */
export const deriveQmsCapaOwnerInitials = (capaOwner: string): string => {
  const parts = capaOwner.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return (parts[0] ?? "").slice(0, 2).toUpperCase();
  }
  return parts
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 10);
};

export type QmsCapaFieldErrors = Partial<
  Record<
    | "correctiveAction"
    | "preventiveAction"
    | "capaOwner"
    | "capaDueDate"
    | "effectivenessCheck"
    | "effectivenessCheckDate",
    string
  >
>;

/** Client-side CAPA validation aligned with API MinLength / required rules. */
export const validateQmsCapaForm = (
  capa: Partial<CAPARecord>,
  options?: { requireFutureDueDate?: boolean },
): QmsCapaFieldErrors => {
  const errors: QmsCapaFieldErrors = {};
  const corrective = capa.correctiveAction?.trim() ?? "";
  const preventive = capa.preventiveAction?.trim() ?? "";
  const owner = capa.capaOwner?.trim() ?? "";
  const effectiveness = capa.effectivenessCheck?.trim() ?? "";
  const dueDate = capa.capaDueDate?.trim() ?? "";

  if (!corrective) {
    errors.correctiveAction = "Corrective action is required";
  } else if (corrective.length < QMS_CAPA_TEXT_MIN_LENGTH) {
    errors.correctiveAction = `Must be at least ${QMS_CAPA_TEXT_MIN_LENGTH} characters`;
  }

  if (!preventive) {
    errors.preventiveAction = "Preventive action is required";
  } else if (preventive.length < QMS_CAPA_TEXT_MIN_LENGTH) {
    errors.preventiveAction = `Must be at least ${QMS_CAPA_TEXT_MIN_LENGTH} characters`;
  }

  if (!owner) {
    errors.capaOwner = "CAPA owner is required";
  }

  if (!dueDate) {
    errors.capaDueDate = "CAPA due date is required";
  } else if (options?.requireFutureDueDate !== false) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate);
    const parsed = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!Number.isNaN(parsed.getTime()) && parsed < today) {
      errors.capaDueDate = "CAPA due date must be in the future";
    }
  }

  if (!effectiveness) {
    errors.effectivenessCheck = "Effectiveness check criteria is required";
  } else if (effectiveness.length < QMS_CAPA_TEXT_MIN_LENGTH) {
    errors.effectivenessCheck = `Must be at least ${QMS_CAPA_TEXT_MIN_LENGTH} characters`;
  }

  return errors;
};

/** Normalize API/ISO dates for `<input type="date" />` (YYYY-MM-DD). */
export const toQmsDateInputValue = (value?: string | null): string => {
  if (!value?.trim()) {
    return "";
  }
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (match?.[1]) {
    return match[1];
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
};
